import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PREDEFINED_VENUES } from '../constants/campusVenues';
import { useAuth } from '../context/AuthContext';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const EVENT_TYPES = ['meetup', 'seminar', 'workshop', 'conference', 'guest_lecture', 'interview', 'other'];

const emptyForm = () => ({
  title: '',
  date: null,
  endDate: null,
  isMultiDay: false,
  venue: '',
  type: 'seminar',
  eventMode: 'offline',
  generatePass: true,
});

export function EventModal({ event, onClose, onSave, onDelete }) {
  const { faculty: currentUser } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [venueSelect, setVenueSelect] = useState('');
  const [venueOther, setVenueOther] = useState('');
  const [typeSelect, setTypeSelect] = useState('seminar');
  const [typeOther, setTypeOther] = useState('');
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]); // Array of Faculty objects
  const [collabEmail, setCollabEmail] = useState('');
  const [collabLoading, setCollabLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const isOwner = useMemo(() => {
    if (!event?._id) return true; // New event
    const eventFacultyId = (event.faculty && typeof event.faculty === 'object') ? event.faculty._id : event.faculty;
    const currentUserId = currentUser?._id || currentUser?.id;
    return String(eventFacultyId) === String(currentUserId);
  }, [event, currentUser]);

  // Fetch suggestions
  useEffect(() => {
    if (collabEmail.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/auth/search', { params: { q: collabEmail } });
        if (res.data.success) {
          setSuggestions(res.data.faculties || []);
          setShowSuggestions(res.data.faculties?.length > 0);
        }
      } catch (err) {
        console.error('Error fetching suggestions', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [collabEmail]);

  const handleAddCollaborator = async () => {
    const email = collabEmail.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid university email');
      return;
    }
    
    // Check if already added
    if (collaborators.some(c => c.email === email)) {
      toast.error('Collaborator already added');
      return;
    }

    setCollabLoading(true);
    try {
      const res = await api.get('/auth/lookup', { params: { email } });
      if (res.data.success && res.data.faculty) {
        setCollaborators(p => [...p, res.data.faculty]);
        setCollabEmail('');
        toast.success('Collaborator added!');
      } else {
        toast.error('Faculty member not found');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Faculty member not found with this university email');
      } else {
        toast.error('Error looking up faculty');
      }
    } finally {
      setCollabLoading(false);
    }
  };

  const removeCollaborator = (id) => {
    setCollaborators(p => p.filter(c => c._id !== id));
  };

  const [showCollabSection, setShowCollabSection] = useState(false);

  useEffect(() => {
    if (event?._id) {
      setForm({
        title: event.title || '',
        isMultiDay: event.isMultiDay || false,
        date: event.date ? new Date(event.date) : null,
        endDate: event.endDate ? new Date(event.endDate) : null,
        eventMode: event.eventMode || 'offline'
      });
      
      const v = event.venue || '';
      if (PREDEFINED_VENUES.includes(v)) {
        setVenueSelect(v);
        setVenueOther('');
      } else if (v === 'Online') {
        setVenueSelect('');
        setVenueOther('');
      } else {
        setVenueSelect('__other__');
        setVenueOther(v);
      }

      const t = event.type || 'seminar';
      if (EVENT_TYPES.includes(t)) {
        setTypeSelect(t);
        setTypeOther('');
      } else {
        setTypeSelect('other');
        setTypeOther(t);
      }

      setCollaborators(event.collaborators || []);
      setCollabEmail('');
      setShowCollabSection((event.collaborators || []).length > 0);
    } else {
      setForm({
        title: '',
        isMultiDay: false,
        date: null,
        endDate: null,
        eventMode: 'offline'
      });
      setTypeSelect('seminar');
      setTypeOther('');
      setVenueSelect('');
      setVenueOther('');
      setCollaborators([]);
      setCollabEmail('');
      setShowCollabSection(false);
    }
  }, [event]);

  const isAdmin = ['iqac', 'super_admin'].includes(currentUser?.role);
  
  // Logical locks for UI/Validation
  const { isLocked, detailsLocked } = useMemo(() => {
    if (!event?._id) return { isLocked: false, detailsLocked: false };
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(event.date);
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDate = event.isMultiDay && event.endDate ? new Date(event.endDate) : new Date(event.date);
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    
    return { 
      isLocked: today > endDay, 
      detailsLocked: today > endDay 
    };
  }, [event]);

  // Admins can bypass these locks
  const effectivelyLocked = (isLocked || detailsLocked) && !isAdmin;

  const set = (key, val) => {
    if (effectivelyLocked) return;
    setForm((p) => ({ ...p, [key]: val }));
  };

  const resolvedVenue = venueSelect === '__other__' ? venueOther.trim() : venueSelect;
  const resolvedType = typeSelect === 'other' ? (typeOther.trim() || 'other') : typeSelect;

  const conflictingBookings = useMemo(() => {
    const list = bookings || [];
    if (!event?._id) return list;
    return list.filter((b) => String(b._id) !== String(event._id));
  }, [bookings, event]);

  useEffect(() => {
    const d = form.date ? new Date(form.date.getTime() - form.date.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '';
    if (!resolvedVenue || !d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      setBookings([]);
      return;
    }

    let cancel = false;
    setBookingsLoading(true);
    api
      .get('/venues/bookings', { params: { venue: resolvedVenue, date: d } })
      .then((r) => {
        if (!cancel) setBookings(r.data.bookings || []);
      })
      .catch(() => {
        if (!cancel) setBookings([]);
      })
      .finally(() => {
        if (!cancel) setBookingsLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [resolvedVenue, form.date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.eventMode !== 'online') {
      if (!venueSelect) {
        toast.error('Select a venue');
        return;
      }
      if (venueSelect === '__other__' && !venueOther.trim()) {
        toast.error('Enter the other venue name');
        return;
      }
    }
    if (typeSelect === 'other' && !typeOther.trim()) {
      toast.error('Enter the custom event type');
      return;
    }
    if (form.isMultiDay && (!form.date || !form.endDate)) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (form.isMultiDay && form.endDate < form.date) {
      toast.error('End date cannot be before start date');
      return;
    }
    const collaboratorsPayload = collaborators.map(c => c._id);
    const payload = { 
      ...form, 
      venue: form.eventMode === 'online' ? 'Online' : resolvedVenue, 
      type: resolvedType, 
      collaborators: collaboratorsPayload 
    };

    setLoading(true);
    try {
      if (event?._id) {
        await api.put(`/events/${event._id}`, payload);
        toast.success('Event updated!');
      } else {
        await api.post('/events', payload);
        toast.success('Event created!');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{event?._id ? 'Edit event' : 'Create event'}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
          <div className="modal-body" style={{ padding: '1.5rem 2rem' }}>
            {/* Lock Banner */}
            {isLocked && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '14px', display: 'flex', gap: '0.875rem', alignItems: 'center' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#ef4444', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.875rem', color: '#991b1b' }}>Event Concluded</div>
                  <div style={{ fontSize: '0.75rem', color: '#b91c1c', opacity: 0.8 }}>This event has ended. Details and collaborators are now read-only.</div>
                </div>
              </div>
            )}
            {!isLocked && detailsLocked && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '14px', display: 'flex', gap: '0.875rem', alignItems: 'center' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.875rem', color: '#92400e' }}>Details Locked</div>
                  <div style={{ fontSize: '0.75rem', color: '#b45309', opacity: 0.8 }}>
                    {event.isApproved ? 'Approved by AO. ' : 'Event has started. '}
                    Only collaborators can be modified.
                  </div>
                </div>
              </div>
            )}
            {/* Section 1: Basic Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="field">
                <label htmlFor="ev-title" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>Event Title *</label>
                <input
                  id="ev-title"
                  name={`ev-title-${event?._id || 'new'}`}
                  autoComplete="off"
                  required
                  placeholder="e.g. Industry expert talk on AI"
                  value={form.title}
                  onChange={(ev) => set('title', ev.target.value)}
                  disabled={effectivelyLocked}
                  style={{ height: '3.75rem', borderRadius: '14px', fontSize: '1rem', border: '2px solid #e2e8f0', background: effectivelyLocked ? '#f1f5f9' : '#f8fafc', color: effectivelyLocked ? '#64748b' : '#1e293b' }}
                />
              </div>

              <div className="grid-2">
                <div className="field">
                  <label htmlFor="ev-type-preset" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>Event Type *</label>
                  <select id="ev-type-preset" value={typeSelect} onChange={(ev) => setTypeSelect(ev.target.value)} disabled={effectivelyLocked} style={{ height: '3.75rem', borderRadius: '14px', border: '2px solid #e2e8f0', background: effectivelyLocked ? '#f1f5f9' : '#f8fafc', padding: '0 1rem', color: effectivelyLocked ? '#64748b' : '#1e293b' }}>
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="ev-mode" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>Event Mode *</label>
                  <select id="ev-mode" value={form.eventMode} onChange={(ev) => set('eventMode', ev.target.value)} disabled={effectivelyLocked} style={{ height: '3.75rem', borderRadius: '14px', border: '2px solid #e2e8f0', background: effectivelyLocked ? '#f1f5f9' : '#f8fafc', padding: '0 1rem', color: effectivelyLocked ? '#64748b' : '#1e293b' }}>
                    <option value="offline">Offline</option>
                    <option value="online">Online</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>

              {typeSelect === 'other' && (
                <div className="field fade-in">
                  <label htmlFor="ev-type-other" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>Custom event type *</label>
                  <input
                    id="ev-type-other"
                    name="ev-type-custom"
                    autoComplete="off"
                    required
                    placeholder="e.g. Hackathon, Designathon etc"
                    value={typeOther}
                    onChange={(ev) => setTypeOther(ev.target.value)}
                    disabled={effectivelyLocked}
                    style={{ height: '3.5rem', borderRadius: '14px', background: effectivelyLocked ? '#f1f5f9' : '#fff' }}
                  />
                </div>
              )}

              <div className="grid-2">
                <div className="field">
                  <label style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>Event Duration *</label>
                  <div style={{ 
                    display: 'inline-flex', 
                    background: effectivelyLocked ? '#f1f5f9' : '#f1f5f9', 
                    padding: '4px', 
                    borderRadius: '14px',
                    border: '2px solid #e2e8f0',
                    width: '100%',
                    height: '3.75rem',
                    alignItems: 'stretch'
                  }}>
                    <button
                      type="button"
                      onClick={() => { if (!effectivelyLocked) { set('isMultiDay', false); set('endDate', null); } }}
                      disabled={effectivelyLocked}
                      style={{
                        flex: 1,
                        borderRadius: '11px',
                        border: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 800,
                        cursor: effectivelyLocked ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        background: !form.isMultiDay ? 'white' : 'transparent',
                        color: !form.isMultiDay ? 'var(--navy)' : '#64748b',
                        boxShadow: !form.isMultiDay ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                        opacity: effectivelyLocked && form.isMultiDay ? 0.5 : 1
                      }}
                    >
                      One Day
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (!effectivelyLocked) set('isMultiDay', true); }}
                      disabled={effectivelyLocked}
                      style={{
                        flex: 1,
                        borderRadius: '11px',
                        border: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 800,
                        cursor: effectivelyLocked ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        background: form.isMultiDay ? 'white' : 'transparent',
                        color: form.isMultiDay ? 'var(--navy)' : '#64748b',
                        boxShadow: form.isMultiDay ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                        opacity: effectivelyLocked && !form.isMultiDay ? 0.5 : 1
                      }}
                    >
                      Multi Days
                    </button>
                  </div>
                </div>

                {form.eventMode !== 'online' && (
                  <div className="field">
                    <label htmlFor="ev-venue-preset" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>Venue *</label>
                    <select
                      id="ev-venue-preset"
                      required
                      value={venueSelect}
                      onChange={(ev) => setVenueSelect(ev.target.value)}
                      disabled={effectivelyLocked}
                      style={{ height: '3.75rem', borderRadius: '14px', border: '2px solid #e2e8f0', background: effectivelyLocked ? '#f1f5f9' : '#f8fafc', padding: '0 1rem', color: effectivelyLocked ? '#64748b' : '#1e293b' }}
                    >
                      <option value="">Select venue…</option>
                      {PREDEFINED_VENUES.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                      <option value="__other__"> Other</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="field">
                <label htmlFor="ev-date" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>{form.isMultiDay ? 'Event Range *' : 'Event Date *'}</label>
                <div className="datepicker-premium-wrapper">
                  <DatePicker
                    id="ev-date"
                    selected={form.date}
                    onChange={(dates) => {
                      if (form.isMultiDay) {
                        const [start, end] = dates;
                        setForm(p => ({ ...p, date: start, endDate: end }));
                      } else {
                        set('date', dates);
                      }
                    }}
                    startDate={form.date}
                    endDate={form.endDate}
                    selectsRange={form.isMultiDay}
                    dateFormat="dd-MM-yyyy"
                    placeholderText={form.isMultiDay ? "Select start and end dates" : "Select date"}
                    className="react-datepicker-custom"
                    wrapperClassName="date-picker-full-width"
                    required
                    autoComplete="off"
                    monthsShown={1}
                    disabled={detailsLocked || isLocked}
                    renderCustomHeader={({ date, decreaseMonth, increaseMonth, nextMonthButtonDisabled, prevMonthButtonDisabled }) => (
                      <div className="dp-custom-header">
                        <button type="button" className="dp-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                        <span className="dp-month-label">
                          {date.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </span>
                        <button type="button" className="dp-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
                      </div>
                    )}
                  />
                  <div className="dp-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  </div>
                </div>
              </div>

              {form.eventMode !== 'online' && venueSelect === '__other__' && (
                <div className="field fade-in">
                  <label htmlFor="ev-venue-other" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.625rem', display: 'block' }}>Other venue name *</label>
                  <input
                    id="ev-venue-other"
                    name="ev-venue-custom"
                    autoComplete="off"
                    required
                    placeholder="e.g. Lab B204, A104 etc"
                    value={venueOther}
                    onChange={(ev) => setVenueOther(ev.target.value)}
                    disabled={detailsLocked || isLocked}
                    style={{ height: '3.5rem', borderRadius: '14px', background: detailsLocked || isLocked ? '#f1f5f9' : '#fff' }}
                  />
                </div>
              )}
            </div>

            {form.eventMode !== 'online' && resolvedVenue && form.date && (
              <div style={{ 
                marginTop: '1.25rem', 
                padding: '1rem 1.25rem', 
                background: '#fffbeb', 
                borderRadius: '14px', 
                border: '1px solid #fef3c7', 
                borderLeft: '4px solid #f59e0b',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.75rem' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  <span style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#b45309' }}>
                    Venue schedule on this date
                  </span>
                </div>
                {bookingsLoading && <p style={{ fontSize: '0.8125rem', color: '#d97706', fontWeight: 500 }}>Checking calendar…</p>}
                {!bookingsLoading && conflictingBookings.length === 0 && (
                  <p style={{ fontSize: '0.8125rem', color: '#6b7280', fontWeight: 500 }}>No other events use this venue on this date.</p>
                )}
                {!bookingsLoading && conflictingBookings.length > 0 && (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {conflictingBookings.map((b) => (
                      <li key={b._id} style={{ 
                        fontSize: '0.875rem', 
                        color: '#4b5563', 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: '0.75rem',
                        padding: '0.5rem 0'
                      }}>
                        <div style={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          background: '#f59e0b',
                          marginTop: '0.4rem',
                          flexShrink: 0
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.4 }}>
                            {b.title}
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem', 
                            marginTop: '0.25rem',
                            fontSize: '0.75rem',
                            color: '#64748b'
                          }}>
                            <span>hosted by <strong>{b.faculty?.name || 'Faculty'}</strong></span>
                            <span style={{ color: '#d1d5db' }}>•</span>
                            <span style={{ textTransform: 'capitalize' }}>{b.type?.replace('_', ' ')}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* --- Integrated Collaborator Section --- */}
            {isOwner && (
              <div style={{ 
                marginTop: '2rem', 
                background: '#f8fafc', 
                borderRadius: '20px', 
                border: '2px solid #e2e8f0',
                overflow: 'visible', // Changed to visible for dropdown
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                {/* Header with Toggle */}
                <div style={{ 
                  padding: '1.25rem 1.5rem', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  background: showCollabSection ? '#f1f5f9' : 'transparent',
                  borderBottom: showCollabSection ? '1px solid #e2e8f0' : 'none',
                  borderRadius: showCollabSection ? '20px 20px 0 0' : '20px',
                  transition: 'all 0.2s'
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '10px', 
                    background: showCollabSection ? 'var(--navy)' : '#e2e8f0', 
                    color: showCollabSection ? 'white' : '#64748b', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    transition: 'all 0.3s'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: '#1e293b' }}>Event Collaborators</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Allow other faculty members to help manage this event</div>
                  </div>
                </div>
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', flexShrink: 0 }}>
                  <input 
                    type="checkbox" 
                    checked={showCollabSection} 
                    onChange={e => setShowCollabSection(e.target.checked)}
                    disabled={isLocked}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: showCollabSection ? 'var(--navy)' : '#cbd5e1',
                    transition: '0.4s',
                    borderRadius: '34px'
                  }}>
                    <span style={{
                      position: 'absolute',
                      height: '18px', width: '18px',
                      left: '4px', bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%',
                      transform: showCollabSection ? 'translateX(26px)' : 'translateX(0)'
                    }} />
                  </span>
                </label>
              </div>

              {/* Expandable Content */}
              {showCollabSection && (
                <div style={{ 
                  padding: '1.75rem', 
                  paddingBottom: showSuggestions ? '120px' : '1.75rem', // Added spacer for dropdown
                  animation: 'fadeIn 0.3s ease-out',
                  transition: 'padding-bottom 0.2s'
                }}>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: collaborators.length > 0 ? '2rem' : 0 }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input 
                        type="email" 
                        placeholder="Enter faculty university email..." 
                        value={collabEmail} 
                        onChange={e => setCollabEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCollaborator())}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        onFocus={() => collabEmail.length >= 2 && setShowSuggestions(true)}
                        disabled={isLocked}
                        style={{ 
                          width: '100%', 
                          height: '3.75rem', 
                          paddingLeft: '1.25rem', 
                          borderRadius: '14px', 
                          border: '2px solid #cbd5e1',
                          fontSize: '1rem',
                          background: isLocked ? '#f1f5f9' : 'white',
                          color: isLocked ? '#64748b' : '#1e293b',
                          transition: 'border-color 0.2s'
                        }}
                      />
                      {showSuggestions && (
                        <div className="country-combobox-list" style={{
                          top: 'calc(100% + 12px)',
                          background: 'white',
                          zIndex: 2000,
                          maxHeight: '220px',
                          overflowY: 'auto'
                        }}>
                          {suggestions.map(s => (
                            <button
                              key={s._id}
                              type="button"
                              className="country-combobox-opt"
                              onClick={() => {
                                setCollabEmail(s.email);
                                setShowSuggestions(false);
                              }}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}
                            >
                              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--navy)' }}>{s.name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', fontWeight: 500 }}>{s.email}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button 
                      type="button" 
                      className="btn btn-navy" 
                      onClick={handleAddCollaborator}
                      disabled={collabLoading || isLocked}
                      style={{ 
                        height: '3.75rem', 
                        padding: '0 2rem', 
                        borderRadius: '14px', 
                        fontWeight: 800, 
                        boxShadow: isLocked ? 'none' : '0 8px 16px rgba(0, 95, 167, 0.15)', 
                        opacity: isLocked ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {collabLoading ? <span className="spinner" /> : (
                        <>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                          Add
                        </>
                      )}
                    </button>
                  </div>

                  {collaborators.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                      {collaborators.map(c => (
                        <div 
                          key={c._id} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '1rem', 
                            padding: '1rem 1.25rem', 
                            background: '#ffffff', 
                            borderRadius: '16px', 
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                            animation: 'fadeIn 0.3s ease-out'
                          }}
                        >
                          <div style={{ 
                            width: '2.75rem', 
                            height: '2.75rem', 
                            borderRadius: '12px', 
                            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', 
                            color: '#1e40af', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontWeight: 900, 
                            fontSize: '1rem',
                            flexShrink: 0
                          }}>
                            {c.name?.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                            <div style={{ fontSize: '0.8125rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.department && c.department !== 'General' && c.department !== 'University Faculty' ? c.department : ''}</div>
                          </div>
                          <button 
                            type="button" 
                            onClick={() => removeCollaborator(c._id)}
                            disabled={isLocked}
                            style={{ 
                              background: '#fff1f2', 
                              border: 'none', 
                              color: '#e11d48', 
                              cursor: isLocked ? 'not-allowed' : 'pointer', 
                              padding: '8px',
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                              opacity: isLocked ? 0.4 : 1,
                              flexShrink: 0
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
          <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
            {currentUser?.role === 'super_admin' && event?._id && (
              <button 
                type="button" 
                className="btn btn-red" 
                style={{ marginRight: 'auto' }}
                onClick={() => setShowConfirm(true)}
              >
                Delete Event
              </button>
            )}
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-navy" disabled={loading || (isLocked && !!event?._id)}>
              {loading ? (
                <>
                  <span className="spinner" /> Saving…
                </>
              ) : isLocked && event?._id ? (
                'Locked'
              ) : event?._id ? (
                'Update event'
              ) : (
                'Create event'
              )}
            </button>
          </div>
        </form>

        <DeleteConfirmModal 
          isOpen={showConfirm}
          title="Delete Event?"
          message={`Are you sure you want to delete "${event?.title}"? This will also delete all associated guest passes and cannot be undone.`}
          loading={deleteLoading}
          onCancel={() => setShowConfirm(false)}
          onConfirm={async () => {
            setDeleteLoading(true);
            await onDelete(event);
            setDeleteLoading(false);
            setShowConfirm(false);
            onClose();
          }}
        />
      </div>
    </div>
  );
}

export default function Events() {
  const { faculty } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMode, setFilterMode] = useState('all');

  const isAdmin = ['iqac', 'super_admin'].includes(faculty?.role);

  const load = () => {
    setLoading(true);
    api
      .get('/events')
      .then((r) => setEvents(r.data.events || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleEdit = (e) => {
    setEditEvent(e);
    setShowModal(true);
  };
  const handleNew = () => {
    setEditEvent(null);
    setShowModal(true);
  };
  const handleSave = () => {
    setShowModal(false);
    load();
  };

  const handleDelete = async (ev) => {
    try {
      const res = await api.delete(`/events/${ev._id}`);
      if (res.data.success) {
        toast.success('Event deleted');
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete event');
    }
  };

  const typeColor = {
    meetup: '#7c3aed',
    seminar: '#005fa7',
    workshop: '#059669',
    conference: '#d97706',
    guest_lecture: '#be185d',
    interview: '#0891b2',
    other: '#64748b',
  };

  const filtered = events.filter(ev => {
    const matchesSearch = search.trim() ? (
      ev.title.toLowerCase().includes(search.toLowerCase()) ||
      ev.venue?.toLowerCase().includes(search.toLowerCase()) ||
      ev.type?.toLowerCase().includes(search.toLowerCase())
    ) : true;

    const matchesType = filterType === 'all' || ev.type === filterType;
    const matchesMode = filterMode === 'all' || ev.eventMode === filterMode;

    return matchesSearch && matchesType && matchesMode;
  }).sort((a, b) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getEndDate = (e) => {
      const start = new Date(e.date);
      return e.isMultiDay && e.endDate ? new Date(e.endDate) : start;
    };

    const endA = getEndDate(a);
    const endB = getEndDate(b);
    
    const isFinishedA = endA < today;
    const isFinishedB = endB < today;

    // Move finished events to the bottom
    if (isFinishedA && !isFinishedB) return 1;
    if (!isFinishedA && isFinishedB) return -1;
    
    // Within same group, sort by date descending (latest first)
    return new Date(b.date) - new Date(a.date);
  });

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">My events</div>
          <div className="page-subtitle">
            {filtered.length} event{filtered.length !== 1 ? 's' : ''} found
          </div>
        </div>
        <div className="page-actions">
          {faculty?.role !== 'ao' && (
            <button type="button" className="btn btn-navy" onClick={handleNew}>
              + Create event
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        <div className="card toolbar-fixed" style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', background: 'white', padding: '0.75rem 1rem', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
          <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
            <div style={{ position: 'absolute', left: '1rem', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </div>
            <input
              className="toolbar-input-clean"
              style={{ 
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                borderRadius: '12px',
                border: '1px solid transparent',
                background: '#f8fafc',
                fontSize: '0.9375rem',
                color: '#1e293b',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              placeholder="Search your events…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
            <select 
              value={filterType} 
              onChange={e => setFilterType(e.target.value)}
              style={{ 
                padding: '0.75rem 1rem', 
                borderRadius: '12px', 
                border: '1px solid #e2e8f0', 
                background: 'white', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: '#475569', 
                cursor: 'pointer',
                minWidth: '130px'
              }}
            >
              <option value="all">All Types</option>
              <option value="meetup">Meetup</option>
              <option value="seminar">Seminar</option>
              <option value="workshop">Workshop</option>
              <option value="conference">Conference</option>
              <option value="guest_lecture">Guest Lecture</option>
              <option value="interview">Interview</option>
              <option value="other">Other</option>
            </select>

            <select 
              value={filterMode} 
              onChange={e => setFilterMode(e.target.value)}
              style={{ 
                padding: '0.75rem 1rem', 
                borderRadius: '12px', 
                border: '1px solid #e2e8f0', 
                background: 'white', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: '#475569', 
                cursor: 'pointer',
                minWidth: '130px'
              }}
            >
              <option value="all">All Modes</option>
              <option value="offline">Offline</option>
              <option value="online">Online</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="page-loading">
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon">Start here</div>
              <div className="empty-title">No events yet</div>
              <div className="empty-text">Create an event with a venue and date, then issue passes.</div>
              <button type="button" className="btn btn-navy" style={{ margin: '1rem auto 0', display: 'flex' }} onClick={handleNew}>
                + Create event
              </button>
            </div>
          </div>
        ) : (
          <div className="events-grid">
            {filtered.map((ev) => {
              const strip = typeColor[ev.type] || '#64748b';
              const fillPct = Math.min(100, ((ev.passCount || 0) / ev.maxGuests) * 100);
              return (
                <div key={ev._id} className="card fade-in event-tile" style={{ '--event-strip': strip }}>
                  <div className="event-title-row">
                    <span className={`badge badge-${ev.status}`}>{ev.status}</span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <span
                        className="event-type-pill"
                        style={{
                          color: strip,
                          background: `${strip}14`,
                          border: `1px solid ${strip}44`,
                        }}
                      >
                        {ev.type?.replace('_', ' ')}
                      </span>
                      <span className="event-type-pill" style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>
                        {ev.eventMode}
                        {ev.eventMode === 'hybrid' && (ev.generatePass ? ' (Passes)' : ' (No Passes)')}
                      </span>
                    </div>
                  </div>
                  <h3 className="event-title">{ev.title}</h3>
                  <div className="event-meta-stack">
                    <div className="event-meta-line" style={{ fontWeight: 700, color: 'var(--navy)' }}>
                      {ev.isMultiDay && ev.endDate ? (
                        <>
                          {new Date(ev.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          {' - '}
                          {new Date(ev.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </>
                      ) : (
                        new Date(ev.date).toLocaleDateString('en-IN', { dateStyle: 'full' })
                      )}
                    </div>
                    <div className="event-meta-line">{ev.venue}</div>
                  </div>
                  <div className="event-capacity-bar">
                    <div>
                      <div className="event-cap-num">{ev.passCount || 0}</div>
                      <div className="event-cap-muted">Passes issued</div>
                    </div>
                    <div className="event-capacity-middle">
                      <div className="event-cap-num-muted">{ev.maxGuests}</div>
                      <div className="event-cap-muted">Capacity</div>
                    </div>
                    <div className="event-progress-track">
                      <div className="event-progress-fill" style={{ width: `${fillPct}%` }} />
                    </div>
                  </div>
                  <div className="event-card-actions">
                    {(() => {
                      const now = new Date();
                      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const end = ev.isMultiDay && ev.endDate ? new Date(ev.endDate) : new Date(ev.date);
                      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                      const isLocked = today > endDay;
                      if (isLocked && !isAdmin) return null;
                      return (
                        <button type="button" className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => handleEdit(ev)}>
                          Edit
                        </button>
                      );
                    })()}
                    {faculty?.role === 'super_admin' && (
                      <div style={{ flex: 1 }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && <EventModal event={editEvent} onClose={() => setShowModal(false)} onSave={handleSave} onDelete={handleDelete} />}
    </div>
  );
}
